import React, { useMemo } from 'react'
import { Avatar, Box, Flex, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { AvatarStack, HoverPopover, IAvatarStackItem } from 'components/ui'
import { RootState } from 'constants/interfaces'
import { useUserName } from 'hooks/useUserName'
import { isRtlSelector } from 'redux/selectors/general'

interface AuditorsAvatarsProps {
  auditorIds: string[]
  maxVisible?: number
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

// Matches Chakra core's avatar size scale (theme.sizes) so this maps 1:1 with `<Avatar size=...>`.
const AVATAR_PX: Record<NonNullable<AuditorsAvatarsProps['avatarSize']>, number> = {
  xs: 24,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 96,
}

// Popover row — full name + group per auditor.
const AuditorItem: React.FC<{ uid: string }> = ({ uid }) => {
  const { retailUsersObject, mappedGroupsByUsers } = useSelector((state: RootState) => state.config.config)
  const userName = useUserName(uid)

  const user = retailUsersObject?.[uid]
  const userGroup = mappedGroupsByUsers?.[uid]?.[0] ?? ''

  return (
    <Flex alignItems="center" style={{ gap: '12px' }} py="8px" px="12px">
      <Avatar size="sm" name={userName} src={user?.profile_img_url} />
      <Box>
        <Text fontWeight="600" fontSize="14px">
          {userName}
        </Text>
        <Text fontSize="12px" color="gray.500">
          {userGroup}
        </Text>
      </Box>
    </Flex>
  )
}

// Auditor avatar stack with a hover popover listing every auditor — the shared `AvatarStack` for the
// visual, wrapped in the shared `HoverPopover` for the on-hover detail list.
export const AuditorsAvatars: React.FC<AuditorsAvatarsProps> = ({ auditorIds, maxVisible = 5, avatarSize = 'sm' }) => {
  const { retailUsersObject } = useSelector((state: RootState) => state.config.config)
  const isRtl = useSelector(isRtlSelector)

  const items: IAvatarStackItem[] = useMemo(
    () =>
      (auditorIds || []).map((uid) => {
        const user = retailUsersObject?.[uid]

        return { name: user?.first_name || user?.last_name || 'User', src: user?.profile_img_url }
      }),
    [auditorIds, retailUsersObject]
  )

  if (!auditorIds || auditorIds.length === 0) return null

  return (
    <HoverPopover
      trigger={<AvatarStack items={items} max={maxVisible} size={AVATAR_PX[avatarSize]} isRtl={isRtl} overflowAsText />}
    >
      {auditorIds.map((uid) => (
        <AuditorItem key={uid} uid={uid} />
      ))}
    </HoverPopover>
  )
}
